/**
 * AI Engine — point d'entrée public.
 *
 * Phase 1, incrément 11 — port direct de l'objet `aiEngine` du proto
 * (l. 1096-1282) en module pur testable. **Aucun appel API**, tout est
 * heuristique client-side.
 *
 * Garde-fou critique (CLAUDE.md §2.1) : tous les messages produits
 * (suggestions + score labels + summary) doivent respecter le **ton
 * bienveillant**. Le test `tests/unit/ai-engine-tone.test.ts` vérifie
 * cette propriété sur tous les templates i18n.
 *
 * Consommateurs prévus :
 *   - `vCpHome` (incr. 13) : carrousel suggestions + score affiché.
 *   - `vCpActivities` (incr. 14) : alerte transition demain.
 *   - Notifs Capacitor (Phase 7) : push bienveillant J-1.
 */
import type { AiInputData, Prediction, Suggestion } from './types.js';
import {
  analyzeContext,
  detectPatterns,
  generatePredictions,
  generateWeeklySummary,
  getSituationLabel,
  getSituationScore,
  interpolate,
} from './logic.js';
import {
  acceptSuggestion as memAccept,
  dismissSuggestion as memDismiss,
  getMemory,
} from './memory.js';
import { t } from '../../i18n/index.js';

// Re-exports types et helpers
export type {
  AiContext,
  AiInputData,
  AiMemory,
  Prediction,
  PredictionType,
  SituationScore,
  StressLevel,
  Suggestion,
} from './types.js';
export {
  analyzeContext,
  detectPatterns,
  generatePredictions,
  generateWeeklySummary,
  getSituationLabel,
  getSituationScore,
  isCustodyTransition,
  interpolate,
} from './logic.js';
export { acceptSuggestion, dismissSuggestion, getMemory } from './memory.js';

/**
 * Convertit les prédictions en suggestions affichables (max 3, ton
 * bienveillant). Les suggestions précédemment ignorées par
 * l'utilisateur sont skipées.
 *
 * Regarde au plus 4 prédictions (top par riskLevel — déjà trié).
 */
export function generateSuggestions(predictions: Prediction[]): Suggestion[] {
  const memory = getMemory();
  const suggestions: Suggestion[] = [];

  for (const p of predictions.slice(0, 4)) {
    if (suggestions.length >= 3) break;
    const id = `s_${p.type}`;
    if (memory.ignored.includes(id)) continue;

    const built = buildSuggestionMessage(p);
    if (!built) continue;

    suggestions.push({
      id,
      icon: built.icon,
      message: built.message,
      priority: p.riskLevel > 0.85 ? 'high' : 'medium',
      confidenceScore: p.riskLevel,
    });
  }

  return suggestions;
}

/**
 * Pipeline complet d'analyse pour un foyer co-parentage.
 *
 * 1. Analyse le contexte (snapshot du foyer).
 * 2. Met à jour la mémoire des oublis (`detectPatterns`).
 * 3. Produit les prédictions.
 * 4. Convertit en suggestions affichables.
 * 5. Calcule le score de situation + label.
 *
 * Retourne tout le bundle pour un seul appel depuis `vCpHome`.
 */
export function runAnalysis(
  data: AiInputData,
  now: Date = new Date(),
): {
  context: ReturnType<typeof analyzeContext>;
  predictions: Prediction[];
  suggestions: Suggestion[];
  score: number;
  scoreLabel: string;
  summary: string;
} {
  const context = analyzeContext(data, now);
  const memory = detectPatterns(data, getMemory());
  const predictions = generatePredictions(context, memory);
  const suggestions = generateSuggestions(predictions);
  const score = getSituationScore(data, now);
  return {
    context,
    predictions,
    suggestions,
    score,
    scoreLabel: getSituationLabel(score),
    summary: generateWeeklySummary(data, now),
  };
}

/**
 * Re-export thin wrappers pour le namespace `aiEngine` historique du
 * proto (cohérent avec les call sites Phase 1.13+).
 */
export const aiEngine = {
  analyzeContext,
  detectPatterns,
  generatePredictions,
  generateSuggestions,
  generateWeeklySummary,
  getSituationLabel,
  getSituationScore,
  acceptSuggestion: memAccept,
  dismissSuggestion: memDismiss,
  getMemory,
  runAnalysis,
} as const;

// ── Helpers internes ─────────────────────────────────────────────────

interface BuiltMessage {
  icon: string;
  message: string;
}

/**
 * Construit le contenu localisé d'une suggestion à partir d'une
 * prédiction. Centralise le mapping type → template i18n + emoji.
 *
 * Les messages sont **portés mot pour mot** depuis le proto avec leur
 * ton bienveillant, factorisés via `t(key) + interpolate()`.
 */
function buildSuggestionMessage(p: Prediction): BuiltMessage | null {
  switch (p.type) {
    case 'bag_missing': {
      const items = (p.items ?? []).slice(0, 2).join(', ');
      const isPlural = (p.items ?? []).length > 1;
      const key = isPlural ? 'ai_bag_missing_many' : 'ai_bag_missing_one';
      return { icon: '🎒', message: interpolate(t(key), { items }) };
    }
    case 'sport_prep':
      return {
        icon: '👟',
        message: interpolate(t('ai_sport_prep'), { activity: p.activity ?? '' }),
      };
    case 'medical_prep':
      return {
        icon: '🏥',
        message: interpolate(t('ai_medical_prep'), { activity: p.activity ?? '' }),
      };
    case 'recurring_miss': {
      const item = p.item ?? '';
      const cap = item.charAt(0).toUpperCase() + item.slice(1);
      return { icon: '🔄', message: interpolate(t('ai_recurring_miss'), { item: cap }) };
    }
    case 'high_stress':
      return { icon: '💙', message: t('ai_high_stress') };
    default:
      return null;
  }
}
